import formatCallDuration from '../../utils/formatCallDuration';
import useCallDuration from '../../hooks/useCallDuration';
import React, { useState, useEffect, useRef } from 'react';
import {
    FaMicrophone,
    FaMicrophoneSlash,
    FaVideo,
    FaVideoSlash,
    FaPhoneSlash,
    FaExpand,
    FaCompress,
    FaDesktop,
    FaUsers,
    FaUserPlus,
} from 'react-icons/fa';
import ParticipantPanel from './ParticipantPanel';
import AddMembersModal from './AddMembersModal';
import './AgoraActiveCall.css';

const RemoteVideo = ({ uid, track }) => {
    const container = useRef(null);
    useEffect(() => {
        if (!track) return;
        track.play(container.current);
        return () => track.stop();
    }, [track]);
    return <div id={`remote-user-${uid}`} ref={container} className="remote-video" />;
};

const AgoraActiveCall = ({ meeting, tokens, callType = 'video', onLeave, onDurationChange, onParticipantCountChange, initialAudioMuted = false, initialVideoMuted = false }) => {
    const isVoiceOnly = callType === 'voice';
    const [localAudioMuted, setLocalAudioMuted] = useState(initialAudioMuted);
    const [localVideoMuted, setLocalVideoMuted] = useState(initialVideoMuted);
    const [remoteUsers, setRemoteUsers] = useState([]);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [connectionState, setConnectionState] = useState('connecting');
    const callDuration = useCallDuration(tokens, onDurationChange, connectionState);
    const [isSharingScreen, setIsSharingScreen] = useState(false);
    const [currentScreenSharer, setCurrentScreenSharer] = useState(null);
    const [audioLevel, setAudioLevel] = useState(0);
    const [localSpeaking, setLocalSpeaking] = useState(false);
    const [speakingUids, setSpeakingUids] = useState(new Set());
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [showParticipants, setShowParticipants] = useState(false);
    const [showAddMembers, setShowAddMembers] = useState(false);
    const [invitedUsers, setInvitedUsers] = useState([]);

    const containerRef = useRef(null);
    const localVideoRef = useRef(null);
    const remoteContainerRef = useRef(null);
    const agoraClientRef = useRef(null);
    const localTracksRef = useRef({ audio: null, video: null });
    const screenTrackRef = useRef(null);
    const isMounted = useRef(true);
    const initializingRef = useRef(false);
    const participantNamesRef = useRef(tokens.participants || {});

    // Track creation promise to handle cleanup during creation
    const trackCreationPromiseRef = useRef(null);

    useEffect(() => {
        isMounted.current = true;

        // Load Agora SDK dynamically
        const loadAgoraSDK = async () => {
            const timeSinceLastLeave = Date.now() - (window._lastVideoCallLeaveTime || 0);
            const delay = timeSinceLastLeave < 2000 ? 1500 : 0;

            if (window.AgoraRTC) {
                setTimeout(() => {
                    if (isMounted.current) initializeAgora();
                }, delay);
                return;
            }

            const AGORA_SCRIPT_SRC = 'https://download.agora.io/sdk/release/AgoraRTC_N-4.19.3.js';
            let script = document.querySelector(`script[src="${AGORA_SCRIPT_SRC}"]`);

            if (!script) {
                script = document.createElement('script');
                script.src = AGORA_SCRIPT_SRC;
                script.async = true;
                document.body.appendChild(script);
            }

            const handleLoad = () => {
                setTimeout(() => {
                    if (isMounted.current) initializeAgora();
                }, delay);
            };

            script.addEventListener('load', handleLoad);
            script.addEventListener('error', () => {
                if (isMounted.current) {
                    setConnectionState('failed');
                }
            });

            return () => script.removeEventListener('load', handleLoad);
        };

        const cleanupScriptListener = loadAgoraSDK();

        // Cleanup on unmount
        return () => {
            isMounted.current = false;

            if (cleanupScriptListener && typeof cleanupScriptListener === 'function') {
                cleanupScriptListener();
            }

            // Sync cleanup
            stopTracksSync();

            // Async release
            leaveChannel();

        };
    }, []);

    // Synchronous track stopping for immediate hardware release
    const stopTracksSync = () => {
        const tracks = localTracksRef.current;
        const screenTrack = screenTrackRef.current;

        if (screenTrack) {
            try {
                screenTrack.stop();
                screenTrack.close();
            } catch (e) { /* silent */ }
            screenTrackRef.current = null;
        }

        if (tracks.video) {
            try {
                tracks.video.stop();
                tracks.video.close();
            } catch (e) { /* silent */ }
            tracks.video = null;
        }

        if (tracks.audio) {
            try {
                tracks.audio.stop();
                tracks.audio.close();
            } catch (e) { /* silent */ }
            tracks.audio = null;
        }
    };

    // Update participant count when remote users change
    useEffect(() => {
        const totalParticipants = remoteUsers.length + 1; // +1 for local user
        if (onParticipantCountChange) {
            // Use setTimeout to avoid "Cannot update a component while rendering" warning
            const timer = setTimeout(() => {
                if (isMounted.current) onParticipantCountChange(totalParticipants);
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [remoteUsers.length, onParticipantCountChange]);

    // Listen for fullscreen change
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const initializeAgora = async () => {
        if (initializingRef.current || agoraClientRef.current) return;

        try {
            initializingRef.current = true;
            const AgoraRTC = window.AgoraRTC;
            if (!AgoraRTC || !isMounted.current) {
                initializingRef.current = false;
                return;
            }

            // Suppress Agora internal logs (0: DEBUG, 1: INFO, 2: WARNING, 3: ERROR, 4: NONE)
            AgoraRTC.setLogLevel(3);

            // Create Agora client
            const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
            agoraClientRef.current = client;

            // Set up event listeners
            client.on('user-published', handleUserPublished);
            client.on('user-unpublished', handleUserUnpublished);
            client.on('user-left', handleUserLeft);
            client.on('connection-state-change', (curState) => {
                if (!isMounted.current) return;
                setConnectionState(curState);
                setIsReconnecting(curState === 'RECONNECTING');
            });

            // Join the channel
            await client.join(
                tokens.appId,
                tokens.channel,
                tokens.rtcToken,
                tokens.uid
            );

            if (!isMounted.current) {
                await client.leave();
                initializingRef.current = false;
                return;
            }

            setConnectionState('connected');

            client.enableAudioVolumeIndicator(); // fires 'volume-indicator' every ~2s

            client.on('stream-message', (senderUid, payload) => {
                try {
                    const senderIsHost = senderUid === meeting.host?.id;
                    const msg = JSON.parse(new TextDecoder().decode(payload));
                    if (msg.type === 'mute-request' && senderIsHost && msg.target === String(tokens.uid)) {
                        const audioTrack = localTracksRef.current.audio;
                        if (audioTrack) {
                            audioTrack.setEnabled(false);
                            setLocalAudioMuted(true);
                        }
                    }
                } catch { /* ignore */ }
            });

            client.on('volume-indicator', (volumes) => {
                if (!isMounted.current) return;
                volumes.forEach(({ uid, level }) => {
                    if (uid === 0) {
                        setAudioLevel(Math.min(level / 60, 1));
                        setLocalSpeaking(level > 8);
                    } else {
                        setSpeakingUids(prev => {
                            const next = new Set(prev);
                            if (level > 8) next.add(uid);
                            else next.delete(uid);
                            return next;
                        });
                    }
                });
            });

            // Create local tracks — voice-only skips camera
            if (isVoiceOnly) {
                trackCreationPromiseRef.current = AgoraRTC.createMicrophoneAudioTrack();
                const audioTrack = await trackCreationPromiseRef.current;
                trackCreationPromiseRef.current = null;

                if (!isMounted.current) {
                    audioTrack.stop();
                    audioTrack.close();
                    await client.leave();
                    initializingRef.current = false;
                    return;
                }

                if (localTracksRef.current.audio) stopTracksSync();
                localTracksRef.current = { audio: audioTrack, video: null };

                if (initialAudioMuted && audioTrack) await audioTrack.setEnabled(false);
                await client.publish([audioTrack]);
            } else {
                trackCreationPromiseRef.current = AgoraRTC.createMicrophoneAndCameraTracks();
                const [audioTrack, videoTrack] = await trackCreationPromiseRef.current;
                trackCreationPromiseRef.current = null;

                if (!isMounted.current) {
                    audioTrack.stop();
                    audioTrack.close();
                    videoTrack.stop();
                    videoTrack.close();
                    await client.leave();
                    initializingRef.current = false;
                    return;
                }

                if (localTracksRef.current.audio || localTracksRef.current.video) stopTracksSync();
                localTracksRef.current = { audio: audioTrack, video: videoTrack };

                if (localVideoRef.current) videoTrack.play(localVideoRef.current);
                if (initialAudioMuted && audioTrack) await audioTrack.setEnabled(false);
                if (initialVideoMuted && videoTrack) await videoTrack.setEnabled(false);
                await client.publish([audioTrack, videoTrack]);
            }
            initializingRef.current = false;
        } catch (error) {
            console.error('Failed to initialize Agora:', error);
            initializingRef.current = false;
            if (isMounted.current) {
                setConnectionState('failed');
            }
        }
    };

    const handleUserPublished = async (user, mediaType) => {
        const client = agoraClientRef.current;
        if (!client) return;
        await client.subscribe(user, mediaType);
        if (!isMounted.current || agoraClientRef.current !== client) return;

        if (mediaType === 'video') {
            setRemoteUsers(prev => {
                const exists = prev.find(u => u.uid === user.uid);
                if (exists) return prev.map(u => u.uid === user.uid ? { ...u, hasVideo: true, videoTrack: user.videoTrack } : u);
                return [...prev, { uid: user.uid, hasVideo: true, hasAudio: false, videoTrack: user.videoTrack }];
            });
        }

        if (mediaType === 'audio') {
            setRemoteUsers(prev => {
                const exists = prev.find(u => u.uid === user.uid);
                if (exists) return prev.map(u => u.uid === user.uid ? { ...u, hasAudio: true } : u);
                return [...prev, { uid: user.uid, hasVideo: false, hasAudio: true }];
            });
            user.audioTrack.play();
        }
    };

    const handleUserUnpublished = (user, mediaType) => {
        if (mediaType === 'video') {
            setRemoteUsers(prev => prev.map(u => u.uid === user.uid ? { ...u, hasVideo: false } : u));
        }
        if (mediaType === 'audio') {
            setRemoteUsers(prev => prev.map(u => u.uid === user.uid ? { ...u, hasAudio: false } : u));
        }
    };

    const handleUserLeft = (user) => {
        setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
    };

    const handleMuteRequest = async (targetIdentity) => {
        const client = agoraClientRef.current;
        if (!client) return;
        const payload = new TextEncoder().encode(
            JSON.stringify({ type: 'mute-request', target: targetIdentity })
        );
        try {
            await client.sendStreamMessage(payload);
        } catch (e) { console.error('sendStreamMessage failed:', e); }
    };

    const toggleAudio = async () => {
        const audioTrack = localTracksRef.current.audio;
        if (!audioTrack) return;
        await audioTrack.setEnabled(localAudioMuted);
        setLocalAudioMuted(!localAudioMuted);
    };

    const toggleVideo = async () => {
        const videoTrack = localTracksRef.current.video;
        if (!videoTrack) return;
        await videoTrack.setEnabled(localVideoMuted);
        setLocalVideoMuted(!localVideoMuted);
    };

    const toggleScreenShare = async () => {
        const client = agoraClientRef.current;
        if (!client) return;
        const AgoraRTC = window.AgoraRTC;
        if (!AgoraRTC) return;

        if (isSharingScreen) {
            await stopScreenShare();
        } else {
            try {
                const screenTrack = await AgoraRTC.createScreenVideoTrack({
                    encoderConfig: '1080p_1',
                }, 'auto');
                screenTrackRef.current = screenTrack;
                const videoTrack = localTracksRef.current.video;
                if (videoTrack) await client.unpublish([videoTrack]);
                await client.publish(screenTrack);
                setIsSharingScreen(true);
                setCurrentScreenSharer(tokens.uid);
                screenTrack.on('track-ended', () => stopScreenShare());
            } catch (error) {
                console.error('Screen share error:', error);
            }
        }
    };

    const stopScreenShare = async () => {
        const client = agoraClientRef.current;
        const screenTrack = screenTrackRef.current;
        if (!client || !screenTrack) return;
        try {
            await client.unpublish(screenTrack);
            screenTrack.stop();
            screenTrack.close();
            screenTrackRef.current = null;
            const videoTrack = localTracksRef.current.video;
            if (videoTrack) await client.publish([videoTrack]);
            setIsSharingScreen(false);
            if (currentScreenSharer === tokens.uid) setCurrentScreenSharer(null);
        } catch (error) {
            console.error('Error stopping screen share:', error);
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            if (containerRef.current) {
                containerRef.current.requestFullscreen();
                setIsFullscreen(true);
            }
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const leaveChannel = async () => {
        const client = agoraClientRef.current;
        const tracks = localTracksRef.current;

        if (client) {
            try {
                const publishedTracks = [];
                if (tracks.audio) publishedTracks.push(tracks.audio);
                if (tracks.video) publishedTracks.push(tracks.video);
                if (screenTrackRef.current) publishedTracks.push(screenTrackRef.current);

                if (publishedTracks.length > 0) {
                    await client.unpublish(publishedTracks);
                }
            } catch (e) { /* silent */ }
        }

        stopTracksSync();

        if (client) {
            try {
                await client.leave();
            } catch (err) {
                console.error('Error leaving channel:', err);
            }
            agoraClientRef.current = null;
        }
    };

    const handleLeave = async () => {
        window._lastVideoCallLeaveTime = Date.now();
        stopTracksSync();
        await leaveChannel();
        onLeave();
    };

    const buildAgoraParticipants = () => {
        const userName = tokens.guestName || (() => {
            try { return JSON.parse(localStorage.getItem('user') || '{}').name || 'You'; } catch { return 'You'; }
        })();

        const localEntry = {
            identity: String(tokens.uid),
            name: userName,
            isMuted: localAudioMuted,
            isSpeaking: localSpeaking,
            role: meeting.permissions?.is_host ? 'host' : 'participant',
        };

        const remoteEntries = remoteUsers.map(u => ({
            identity: String(u.uid),
            name: participantNamesRef.current[u.uid] || `User ${u.uid}`,
            isMuted: !u.hasAudio,
            isSpeaking: speakingUids.has(u.uid),
            role: 'participant',
        }));

        const activeIds = new Set([String(tokens.uid), ...remoteUsers.map(u => String(u.uid))]);
        const invitedEntries = invitedUsers
            .filter(u => !activeIds.has(String(u.id)))
            .map(u => ({
                identity: String(u.id),
                name: u.name,
                isMuted: true,
                isSpeaking: false,
                role: 'invited',
            }));

        return [localEntry, ...remoteEntries, ...invitedEntries];
    };

    return (
        <div ref={containerRef} className="active-call-container">
            {isReconnecting && (
                <div className="reconnecting-banner">
                    <span className="reconnecting-spinner" />
                    Reconnecting…
                </div>
            )}
            {connectionState === 'connecting' && (
                <div className="call-connecting-overlay">
                    <p>Connecting...</p>
                </div>
            )}
            {connectionState === 'failed' && (
                <div className="call-connecting-overlay" style={{ flexDirection: 'column', gap: '1rem' }}>
                    <p>Connection failed. Please leave and try again.</p>
                    <button className="control-btn leave danger" onClick={handleLeave} title="Leave call">
                        <FaPhoneSlash />
                    </button>
                </div>
            )}
            <div className="call-timer">{formatCallDuration(callDuration)}</div>
            <div className={`video-grid${isVoiceOnly ? ' voice-only-grid' : ''}`}>
                {isVoiceOnly ? (
                    <div className="voice-call-participants">
                        <div className="voice-participant local">
                            <div className={`voice-avatar ${!localAudioMuted && localSpeaking ? 'speaking' : ''}`}>
                                <FaMicrophone size={28} />
                            </div>
                            <span>You {localAudioMuted && <FaMicrophoneSlash size={12} />}</span>
                        </div>
                        {remoteUsers.length === 0 ? (
                            <div className="empty-call-state"><p>Waiting for others to join...</p></div>
                        ) : (
                            remoteUsers.map(user => (
                                <div key={user.uid} className={`voice-participant ${speakingUids.has(user.uid) ? 'speaking' : ''}`}>
                                    <div className={`voice-avatar ${speakingUids.has(user.uid) ? 'speaking' : ''}`}>
                                        <FaMicrophone size={28} />
                                    </div>
                                    <span>
                                        {participantNamesRef.current[user.uid] || `User ${user.uid}`}
                                        {!user.hasAudio && <FaMicrophoneSlash size={12} />}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                ) : (
                    <>
                        <div ref={remoteContainerRef} className="remote-users">
                            {remoteUsers.length === 0 ? (
                                <div className="empty-call-state">
                                    <p>Waiting for others to join...</p>
                                </div>
                            ) : (
                                remoteUsers.map(user => (
                                    <div key={user.uid} className={`remote-user ${speakingUids.has(user.uid) ? 'speaking' : ''}`}>
                                        <RemoteVideo uid={user.uid} track={user.hasVideo ? user.videoTrack : null} />
                                        {!user.hasVideo && (
                                            <div className="video-off-placeholder">
                                                <FaVideoSlash size={32} />
                                                <span>Camera Off</span>
                                            </div>
                                        )}
                                        <div className="user-info">
                                            <span>{participantNamesRef.current[user.uid] || `User ${user.uid}`}</span>
                                            {!user.hasAudio && <FaMicrophoneSlash />}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="local-user-pip">
                            <div ref={localVideoRef} className="local-video" />
                            {localVideoMuted && (
                                <div className="video-off-overlay">
                                    <FaVideoSlash />
                                </div>
                            )}
                            <div className="local-user-label">
                                You
                                {localAudioMuted && <FaMicrophoneSlash />}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="call-controls">
                <button
                    className={`control-btn ${localAudioMuted ? 'muted' : ''} ${!localAudioMuted && localSpeaking ? 'speaking' : ''}`}
                    style={!localAudioMuted ? { '--mic-level': audioLevel } : undefined}
                    onClick={toggleAudio}
                    title={localAudioMuted ? 'Unmute' : 'Mute'}
                >
                    {localAudioMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
                </button>

                {!isVoiceOnly && (
                    <button
                        className={`control-btn ${localVideoMuted ? 'danger' : ''}`}
                        onClick={toggleVideo}
                        title={localVideoMuted ? 'Turn on camera' : 'Turn off camera'}
                    >
                        {localVideoMuted ? <FaVideoSlash /> : <FaVideo />}
                    </button>
                )}

                {!isVoiceOnly && (
                    <button
                        className={`control-btn ${isSharingScreen ? 'active' : ''}`}
                        onClick={toggleScreenShare}
                        title={isSharingScreen ? 'Stop sharing' : 'Share screen'}
                    >
                        <FaDesktop />
                    </button>
                )}

                <button
                    className="control-btn fullscreen"
                    onClick={toggleFullscreen}
                    title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                >
                    {isFullscreen ? <FaCompress /> : <FaExpand />}
                </button>

                <button
                    className="control-btn leave danger"
                    onClick={handleLeave}
                    title="Leave call"
                >
                    <FaPhoneSlash />
                </button>
            </div>

            <div className="call-icon-sidebar">
                <button
                    className={`sidebar-icon-btn ${showParticipants ? 'active' : ''}`}
                    onClick={() => setShowParticipants(p => !p)}
                    title="Participants"
                >
                    <FaUsers size={16} />
                </button>
                {meeting.permissions?.is_host && (
                    <button
                        className="sidebar-icon-btn"
                        onClick={() => setShowAddMembers(true)}
                        title="Add member"
                    >
                        <FaUserPlus size={16} />
                    </button>
                )}
            </div>

            {showParticipants && (
                <ParticipantPanel
                    participants={buildAgoraParticipants()}
                    localIdentity={String(tokens.uid)}
                    isHost={meeting.permissions?.is_host || false}
                    onMuteRequest={handleMuteRequest}
                    onClose={() => setShowParticipants(false)}
                />
            )}
            {showAddMembers && (
                <AddMembersModal
                    meeting={meeting}
                    existingParticipantIds={[...buildAgoraParticipants().map(p => p.identity), ...invitedUsers.map(u => String(u.id))]}
                    onClose={() => setShowAddMembers(false)}
                    onAdded={(newUsers) => {
                        if (newUsers && newUsers.length) {
                            setInvitedUsers(prev => {
                                const map = new Map(prev.map(u => [u.id, u]));
                                newUsers.forEach(u => map.set(u.id, u));
                                return Array.from(map.values());
                            });
                        }
                        setShowAddMembers(false);
                    }}
                />
            )}
        </div>
    );
};

export default AgoraActiveCall;
